import React, { useCallback, useEffect, useState } from "react";
import { Avatar, Box, Button, CircularProgress, Divider, Link, Typography } from "@mui/material";
import { deleteCommentOfDay, getCommentsForAdminOfDay } from "../../service/service";
import CommentsLightbox from "./CommentsLightbox";
import CommentThread from "./CommentThread";
import FullHistoryModal from "./FullHistoryModal";
import AudioPlayerComponent from "../AudioPlayer";
import { COLORS, REACTION_COLORS } from "./colors";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import StarIcon from "@mui/icons-material/Star";
import LightbulbIcon from "@mui/icons-material/Lightbulb";

const REACTION_META = [
  { key: "insightfulCount", label: "Insightful", icon: ThumbUpIcon, ...REACTION_COLORS.insightful },
  { key: "interestingCount", label: "Interesting", icon: StarIcon, ...REACTION_COLORS.interesting },
  { key: "helpfulCount", label: "Helpful", icon: LightbulbIcon, ...REACTION_COLORS.helpful },
];

const formatDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getUTCFullYear()}`;
};

const FooterStat = ({ icon, value, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {icon}
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Box>
);

// Read-only counterpart to CurrentLiveTip's expanded body — same layout (Title/Description/Audio,
// Comments, Reactions, footer stats + history), minus the inline-edit affordances, since editing
// an upcoming tip already goes through the AddTipModal flow from the row's own Edit button.
const UpcomingTipDetail = ({ tip }) => {
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, focusCommentId: null });
  const [historyOpen, setHistoryOpen] = useState(false);

  const latestDates = [...(tip.shownHistory || [])]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-3);

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    const result = await getCommentsForAdminOfDay(tip._id);
    if (result?.success) {
      setComments(result.data || []);
    }
    setCommentsLoading(false);
  }, [tip._id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleDeleteComment = async (commentId) => {
    await deleteCommentOfDay(commentId);
    fetchComments();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 300px" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Title
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {tip.title}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Description
          </Typography>
          <Typography variant="body2">{tip.description}</Typography>

          {tip.type === "audio" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Audio
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {tip.audioFileName}
              </Typography>
              <AudioPlayerComponent audioUrl={tip.audioUrl} />
            </Box>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

        <Box sx={{ flex: "1 1 340px" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Comments ({tip.commentsCount})
          </Typography>
          {commentsLoading && <CircularProgress size={20} />}
          {!commentsLoading && comments.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No comments yet.
            </Typography>
          )}
          {!commentsLoading &&
            comments.slice(0, 2).map((comment) => (
              <CommentThread
                key={comment._id}
                comment={comment}
                onReply={(selected) => setLightbox({ open: true, focusCommentId: selected._id })}
                onDelete={handleDeleteComment}
              />
            ))}
          {!commentsLoading && comments.length > 0 && (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              sx={{ color: COLORS.link }}
              onClick={() => setLightbox({ open: true, focusCommentId: null })}
            >
              View all {tip.commentsCount} comments
            </Link>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

        <Box sx={{ flex: "0 1 220px" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Reactions
          </Typography>
          {REACTION_META.map((reaction) => {
            const ReactionIcon = reaction.icon;
            return (
              <Box
                key={reaction.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: reaction.bg,
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Avatar sx={{ bgcolor: reaction.color, width: 28, height: 28 }}>
                  <ReactionIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {reaction.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {tip[reaction.key] || 0}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
        <Box
          sx={{
            flex: "1 1 300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            flexWrap: "wrap",
          }}
        >
          <FooterStat icon={<VisibilityIcon color="action" />} value={tip.views} label="total views" />
          <FooterStat icon={<AutorenewIcon color="action" />} value="Shown" label={`${tip.rotations} times`} />
          <FooterStat
            icon={<CalendarTodayIcon color="action" />}
            value="Last shown"
            label={formatDate(tip.lastShownDate) || "Never"}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 560px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Latest dates:
            </Typography>
            {latestDates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Not shown yet
              </Typography>
            )}
            {latestDates.map((entry) => (
              <Typography key={entry.date} variant="body2">
                {formatDate(entry.date)} — {entry.viewsCount} views
              </Typography>
            ))}
          </Box>
          <Button variant="outlined" sx={{ color: COLORS.link, borderColor: COLORS.link }} onClick={() => setHistoryOpen(true)}>
            View Full History
          </Button>
        </Box>
      </Box>

      <CommentsLightbox
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, focusCommentId: null })}
        tipId={tip._id}
        tipTitle={tip.title}
        comments={comments}
        focusCommentId={lightbox.focusCommentId}
        onCommentsChanged={fetchComments}
      />

      <FullHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} tip={tip} />
    </Box>
  );
};

export default UpcomingTipDetail;
